import React, { useState, useEffect } from 'react'
import { Clock, Plus, Trash2, Loader2 } from 'lucide-react'
import { api } from '@/api/client'
import type { ImplTimeEntry, JiraWorklogEntry } from '@/api/types'

const SOURCE_LABELS: Record<ImplTimeEntry['source'], string> = {
  meeting:  'Meeting',
  call:     'Customer Call',
  slack:    'Slack Discussion',
  email:    'Customer Email',
  jira:     'Jira Logged',
  internal: 'Internal Work',
  other:    'Other',
}

const SOURCE_COLORS: Record<ImplTimeEntry['source'], string> = {
  meeting:  '#6366f1',
  call:     '#f59e0b',
  slack:    '#10b981',
  email:    '#3b82f6',
  jira:     '#8b5cf6',
  internal: '#6b7280',
  other:    '#94a3b8',
}

const HOURS_PER_DAY = 7.6

function fmtHours(seconds: number): string {
  const d = seconds / 3600 / HOURS_PER_DAY
  if (d === 0) return '—'
  return d % 1 === 0 ? `${d}d` : `${d.toFixed(1)}d`
}

function fmtHoursFromHours(hours: number): string {
  if (hours === 0) return '—'
  const d = hours / HOURS_PER_DAY
  return d % 1 === 0 ? `${d}d` : `${d.toFixed(1)}d`
}

interface Props {
  jiraKey: string
  jiraTimeSpentSeconds: number | null
  currentUserEmail: string
  currentUserName: string | null
}

export function TimeTrackingPanel({ jiraKey, jiraTimeSpentSeconds, currentUserEmail, currentUserName }: Props) {
  const [entries, setEntries] = useState<ImplTimeEntry[]>([])
  const [worklogs, setWorklogs] = useState<JiraWorklogEntry[]>([])
  const [loadingEntries, setLoadingEntries] = useState(true)
  const [loadingWorklogs, setLoadingWorklogs] = useState(true)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  const [formDate, setFormDate] = useState(() => new Date().toISOString().split('T')[0])
  const [formHours, setFormHours] = useState('')
  const [formSource, setFormSource] = useState<ImplTimeEntry['source']>('meeting')
  const [formDesc, setFormDesc] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoadingEntries(true)
      setLoadingWorklogs(true)
      const [entriesRes, worklogsRes] = await Promise.all([
        api.implTime.getEntries(jiraKey),
        api.implTime.getJiraWorklogs(jiraKey),
      ])
      if (!cancelled) {
        if (entriesRes.success && entriesRes.data) setEntries(entriesRes.data.entries)
        setLoadingEntries(false)
        if (worklogsRes.success && worklogsRes.data) setWorklogs(worklogsRes.data.worklogs)
        setLoadingWorklogs(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [jiraKey])

  const manualHours = entries.reduce((s, e) => s + e.hours, 0)
  const jiraHours = (jiraTimeSpentSeconds ?? 0) / 3600
  const totalHours = manualHours + jiraHours

  async function handleDelete(id: number) {
    setDeletingId(id)
    await api.implTime.deleteEntry(id)
    setEntries((prev) => prev.filter((e) => e.id !== id))
    setDeletingId(null)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)
    const hours = parseFloat(formHours)
    if (!formHours || isNaN(hours) || hours <= 0) {
      setFormError('Enter a valid number of hours (e.g. 1.5)')
      return
    }
    setSaving(true)
    const res = await api.implTime.createEntry({
      jira_key: jiraKey,
      hours,
      source: formSource,
      description: formDesc.trim() || undefined,
      entry_date: formDate,
    })
    if (res.success && res.data) {
      const newEntry: ImplTimeEntry = {
        id: res.data.id,
        jira_key: jiraKey,
        logged_by_email: currentUserEmail,
        logged_by_name: currentUserName,
        hours,
        source: formSource,
        description: formDesc.trim() || null,
        entry_date: formDate,
        created_at: new Date().toISOString(),
      }
      setEntries((prev) => [newEntry, ...prev])
      setFormHours('')
      setFormDesc('')
      setShowForm(false)
    } else {
      setFormError(res.error || 'Failed to save entry')
    }
    setSaving(false)
  }

  // Group worklogs by author for the Jira section
  const worklogsByAuthor = worklogs.reduce<Record<string, { total: number; count: number }>>((acc, w) => {
    if (!acc[w.author]) acc[w.author] = { total: 0, count: 0 }
    acc[w.author].total += w.timeSpentSeconds
    acc[w.author].count++
    return acc
  }, {})

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] p-4 space-y-4">
      {/* Summary header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Clock size={14} className="text-[var(--text-muted)]" />
          <span className="text-[13px] font-semibold text-[var(--text-primary)]">
            Total: {totalHours > 0 ? fmtHoursFromHours(totalHours) : '—'}
          </span>
          {jiraHours > 0 && (
            <span className="text-[11px] text-[#8b5cf6] bg-[#8b5cf6]/10 border border-[#8b5cf6]/20 rounded-full px-2 py-0.5">
              {fmtHoursFromHours(jiraHours)} Jira
            </span>
          )}
          {manualHours > 0 && (
            <span className="text-[11px] text-[#6366f1] bg-[#6366f1]/10 border border-[#6366f1]/20 rounded-full px-2 py-0.5">
              {fmtHoursFromHours(manualHours)} manual
            </span>
          )}
        </div>
        <button
          onClick={() => { setShowForm(!showForm); setFormError(null) }}
          className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-md border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-primary transition-colors cursor-pointer bg-transparent"
        >
          <Plus size={11} />
          Log time
        </button>
      </div>

      {/* Log time form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3 space-y-2.5">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Date</label>
              <input
                type="date"
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
                className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-[12px] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Hours</label>
              <input
                type="number"
                min="0.25"
                max="24"
                step="0.25"
                placeholder="e.g. 1.5"
                value={formHours}
                onChange={(e) => setFormHours(e.target.value)}
                className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-[12px] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-[var(--text-muted)]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Source</label>
              <select
                value={formSource}
                onChange={(e) => setFormSource(e.target.value as ImplTimeEntry['source'])}
                className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-[12px] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary cursor-pointer"
              >
                <option value="meeting">Meeting</option>
                <option value="call">Customer Call</option>
                <option value="slack">Slack Discussion</option>
                <option value="email">Customer Email</option>
                <option value="internal">Internal Work</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">Description</label>
              <input
                type="text"
                placeholder="Optional notes"
                value={formDesc}
                onChange={(e) => setFormDesc(e.target.value)}
                className="bg-[var(--bg-primary)] border border-[var(--border-color)] rounded px-2 py-1 text-[12px] text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-[var(--text-muted)]"
              />
            </div>
          </div>
          {formError && <p className="text-[11px] text-red-400">{formError}</p>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowForm(false); setFormError(null) }}
              className="text-[11px] px-2.5 py-1 rounded-md border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] cursor-pointer bg-transparent transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="text-[11px] px-3 py-1 rounded-md bg-primary text-white font-semibold disabled:opacity-50 cursor-pointer hover:opacity-90 transition-opacity border-none"
            >
              {saving ? 'Saving…' : 'Save entry'}
            </button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Jira worklogs */}
        <div>
          <div className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-2">
            Jira worklogs {!loadingWorklogs && worklogs.length > 0 && `(${worklogs.length})`}
          </div>
          {loadingWorklogs ? (
            <div className="flex items-center gap-1.5 text-[11px] text-[var(--text-muted)]">
              <Loader2 size={11} className="animate-spin" /> Loading…
            </div>
          ) : worklogs.length === 0 ? (
            <p className="text-[11px] text-[var(--text-muted)]/60 italic">No time logged in Jira</p>
          ) : (
            <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
              {/* Author summary */}
              {Object.entries(worklogsByAuthor).map(([author, { total }]) => (
                <div key={author} className="flex items-center justify-between rounded-md px-2.5 py-1.5 bg-[#8b5cf6]/8 border border-[#8b5cf6]/15">
                  <span className="text-[11px] text-[var(--text-primary)] font-medium truncate max-w-[130px]">{author}</span>
                  <span className="text-[11px] font-semibold text-[#8b5cf6] ml-2">{fmtHours(total)}</span>
                </div>
              ))}
              {/* Individual entries */}
              <div className="mt-1.5 space-y-1">
                {worklogs.map((w) => (
                  <div key={w.id} className="flex items-start gap-2 text-[11px] text-[var(--text-muted)] pl-1">
                    <span className="whitespace-nowrap tabular-nums shrink-0">{w.started}</span>
                    <span className="font-semibold text-[var(--text-primary)] shrink-0">{fmtHours(w.timeSpentSeconds)}</span>
                    <span className="shrink-0 text-[var(--text-muted)]/70">{w.author}</span>
                    {w.comment && <span className="truncate text-[var(--text-muted)]/60 italic">{w.comment}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Manual entries */}
        <div>
          <div className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-2">
            Time entries {!loadingEntries && entries.length > 0 && `(${entries.length})`}
          </div>
          {loadingEntries ? (
            <div className="flex items-center gap-1.5 text-[11px] text-[var(--text-muted)]">
              <Loader2 size={11} className="animate-spin" /> Loading…
            </div>
          ) : entries.length === 0 ? (
            <p className="text-[11px] text-[var(--text-muted)]/60 italic">No manual entries yet</p>
          ) : (
            <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
              {entries.map((entry) => {
                const color = SOURCE_COLORS[entry.source]
                return (
                  <div
                    key={entry.id}
                    className="flex items-start gap-2 rounded-md px-2.5 py-1.5 border group"
                    style={{ background: color + '10', borderColor: color + '25' }}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11px] font-semibold" style={{ color }}>{fmtHoursFromHours(entry.hours)}</span>
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                          style={{ background: color + '20', color }}
                        >
                          {SOURCE_LABELS[entry.source]}
                        </span>
                        <span className="text-[10px] text-[var(--text-muted)]">{entry.entry_date}</span>
                      </div>
                      {entry.description && (
                        <p className="text-[11px] text-[var(--text-muted)] mt-0.5 truncate">
                          {entry.source === 'jira' ? (() => {
                            const m = entry.description.match(/^([A-Z]+-\d+):\s*(.*)$/)
                            if (m) return (
                              <>
                                <a
                                  href={`https://frankieone.atlassian.net/browse/${m[1]}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="font-semibold underline-offset-2 hover:underline"
                                  style={{ color: color }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {m[1]}
                                </a>
                                {': '}{m[2]}
                              </>
                            )
                            return entry.description
                          })() : entry.description}
                        </p>
                      )}
                      <p className="text-[10px] text-[var(--text-muted)]/60 mt-0.5">
                        {entry.logged_by_name || entry.logged_by_email}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(entry.id)}
                      disabled={deletingId === entry.id}
                      className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-500 transition-opacity cursor-pointer bg-transparent border-none p-0.5 shrink-0 mt-0.5"
                      title="Delete entry"
                    >
                      {deletingId === entry.id
                        ? <Loader2 size={11} className="animate-spin" />
                        : <Trash2 size={11} />}
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
